import { NavLink, useNavigate } from "react-router";

import EnbrosLogoIcon from "@/assets/icons/admin/enbros-logo-sm.svg?react";
import LayoutDashboardIcon from "@/assets/icons/admin/layout-dashboard.svg?react";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { cn } from "@/lib/utils";

function getInitials(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || "AD";
}

function getDisplayName(email: string): string {
  const local = email.split("@")[0] ?? "Admin";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function AdminSidebar() {
  const navigate = useNavigate();
  const { adminUser, logout } = useAdminAuth();
  const email = adminUser?.email ?? "";
  const initials = email ? getInitials(email) : "AD";
  const displayName = email ? getDisplayName(email) : "Admin";

  async function handleLogout() {
    await logout();
    navigate("/admin", { replace: true });
  }

  return (
    <aside className="flex h-dvh w-[250px] shrink-0 flex-col justify-between border-r border-white/[0.08] bg-[#091424] px-6 py-8">
      <div className="flex w-full flex-col gap-8">
        <NavLink to="/admin" aria-label="Enbros admin home">
          <EnbrosLogoIcon aria-hidden className="h-[14px] w-[73px]" />
        </NavLink>

        <nav className="flex w-full flex-col gap-1" aria-label="Admin">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              cn(
                "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold text-white transition-colors",
                isActive ? "bg-white/5" : "hover:bg-white/[0.03]",
              )
            }
          >
            <LayoutDashboardIcon aria-hidden className="size-[18px] shrink-0" />
            <span className="min-w-0 flex-1">Dashboard</span>
          </NavLink>
        </nav>
      </div>

      <div className="flex w-full flex-col gap-3">
        <div className="flex w-full items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-[18px] bg-[#38bdf8]">
            <span className="text-sm font-bold text-[#0c1b2e]">{initials}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-white">
              {displayName}
            </p>
            <p className="truncate text-[11px] text-[#9194a1]">Admin</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => void handleLogout()}
          className="w-full rounded-lg border border-[#ef4444] px-4 py-2.5 text-left text-[13px] font-medium text-[#ef4444] transition-colors hover:bg-[#ef4444]/10"
        >
          Log out
        </button>
      </div>
    </aside>
  );
}
